import { HttpStatusCode } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { IsoDate } from '../../shared/utils/iso-date';
import { DailyChallengeSessionStatus } from '../models/cache/daily-challenge-cache-entry';
import { isApiErrorStatus } from '../models/responses/api-error-response';
import { QuemSouEuSessionResponse } from '../models/responses/quem-sou-eu-session-response';
import { Theme } from '../models/responses/theme';
import { DailyChallengeCacheService } from './daily-challenge-cache';
import { GameSessionService } from './game-session';
import { QuemSouEuApiService } from './requests/quem-sou-eu';
import { ScreenNavigationService } from './screen-navigation';

/** Resultado de `play()`: a tela de jogo foi aberta, ou o desafio do dia já foi jogado. */
export type DailyPlayOutcome = 'started' | 'already-played';

/**
 * Estado e regras do Desafio Diário, que atravessam telas:
 * escolha do tema (daily-theme) -> escolha do dia (daily-calendar) -> jogo.
 * O tema vem sempre primeiro: os dias exibidos no calendário dependem dele.
 */
@Injectable({ providedIn: 'root' })
export class DailyChallengeService {
  private readonly quemSouEuApi = inject(QuemSouEuApiService);
  private readonly cache = inject(DailyChallengeCacheService);
  private readonly gameSession = inject(GameSessionService);
  private readonly screenNavigation = inject(ScreenNavigationService);

  private readonly currentTheme = signal<Theme | null>(null);
  private readonly currentDate = signal<IsoDate | null>(null);
  readonly theme = this.currentTheme.asReadonly();
  /** Dia do desafio escolhido, sempre `yyyy-mm-dd` (ex: 2026-10-11). */
  readonly date = this.currentDate.asReadonly();

  /** Trocar de tema invalida a data escolhida anteriormente. */
  selectTheme(theme: Theme): void {
    this.currentTheme.set(theme);
    this.currentDate.set(null);
  }

  selectDate(date: IsoDate | null): void {
    this.currentDate.set(date);
  }

  /** Status de cada dia já jogado no tema escolhido (para o calendário). */
  playedDays(): Map<IsoDate, DailyChallengeSessionStatus> {
    const theme = this.currentTheme();
    return theme ? this.cache.statusByDate(theme.id) : new Map();
  }

  /**
   * Abre o desafio do tema/dia escolhidos:
   * - sem cache -> cria a sessão (POST);
   * - VITORIA/DERROTA -> já jogado, não abre;
   * - EM_ANDAMENTO -> retoma (GET); 400 = encerrada, 404 = expirada (cria uma nova).
   * Erros inesperados de HTTP são relançados para a tela tratar.
   */
  async play(): Promise<DailyPlayOutcome> {
    const theme = this.currentTheme();
    const date = this.currentDate();
    if (!theme || !date) {
      throw new Error('Tema e dia do Desafio Diário precisam estar escolhidos.');
    }

    const entry = this.cache.get(date, theme.id);

    if (entry && entry.sessionStatus !== 'EM_ANDAMENTO') {
      return 'already-played';
    }

    if (entry) {
      try {
        const session = await firstValueFrom(this.quemSouEuApi.getSession(entry.idSession));
        this.openGame(session);
        return 'started';
      } catch (error) {
        if (isApiErrorStatus(error, HttpStatusCode.BadRequest)) {
          // Sessão encerrada: mantém a entrada no cache e bloqueia o acesso.
          return 'already-played';
        }
        if (!isApiErrorStatus(error, HttpStatusCode.NotFound)) {
          throw error;
        }
        // 404: sessão inexistente/expirada -> libera um novo POST.
        this.cache.remove(date, theme.id);
      }
    }

    const session = await firstValueFrom(this.quemSouEuApi.startDailySession(theme.id, date));
    // Grava antes de navegar: um F5 logo depois ainda encontra a sessão.
    this.cache.save(session.idSession, theme.id, date, 'EM_ANDAMENTO');
    this.openGame(session);
    return 'started';
  }

  /** Função única para abrir a tela de jogo a partir da sessão, nova ou retomada. */
  private openGame(session: QuemSouEuSessionResponse): void {
    this.gameSession.load(session);
    this.screenNavigation.goToGame(session.theme);
  }
}
